import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, Form } from 'react-bootstrap';
import { Button } from 'primereact/button';
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";

// Search bar + "Filters" panel shared by the Draft / Recurring / Sent invoice tabs.
// Typing is debounced; `onChange` receives the filters once the user pauses.

export const EMPTY_INVOICE_SEARCH = {
    search: '',
    invoiceNo: '',
    client: '',
    description: '',
    invoiceRemarks: '',
    productRemarks: '',
    currency: '',
    paymentStatus: '',
    approvalStatus: '',
    minAmount: '',
    maxAmount: '',
};

// Maps the filters to the query params understood by InvoiceRoutes (empty values omitted)
export const toInvoiceSearchParams = (filters = {}) => {
    const params = {
        search: filters.search,
        invoice_no: filters.invoiceNo,
        Billing_name: filters.client,
        description: filters.description,
        invoiceRemarks: filters.invoiceRemarks,
        productRemarks: filters.productRemarks,
        currency: filters.currency,
        payment_status: filters.paymentStatus,
        approval_status: filters.approvalStatus,
        min_amount: filters.minAmount,
        max_amount: filters.maxAmount,
    };
    Object.keys(params).forEach((key) => {
        if (params[key] === undefined || String(params[key]).trim() === '') delete params[key];
    });
    return params;
};

export const hasInvoiceSearch = (filters = {}) => Object.keys(toInvoiceSearchParams(filters)).length > 0;

const CURRENCIES = ['INR', 'USD', 'GBP', 'EUR', 'AED', 'AUD', 'CAD'];
const PAYMENT_STATUSES = [
    ['Payment_due', 'Payment Due'],
    ['Payment_Completed', 'Payment Completed'],
    ['Partial_Payment', 'Partial Payment'],
    ['Refunded', 'Refunded'],
    ['Not_Applicable', 'Not Applicable'],
];
const APPROVAL_STATUSES = [
    ['Accepted', 'Accepted'],
    ['Rejected', 'Rejected'],
    ['Needs_approval', 'Needs Approval'],
];

const InvoiceSearchBar = ({ onChange, showStatusFilters = false, children }) => {
    const [filters, setFilters] = useState(EMPTY_INVOICE_SEARCH);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const lastEmitted = useRef(JSON.stringify(EMPTY_INVOICE_SEARCH));
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        const timer = setTimeout(() => {
            const serialized = JSON.stringify(filters);
            if (serialized === lastEmitted.current) return;
            lastEmitted.current = serialized;
            onChangeRef.current(filters);
        }, 400);
        return () => clearTimeout(timer);
    }, [filters]);

    const update = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

    const advancedKeys = Object.keys(EMPTY_INVOICE_SEARCH).filter((key) => key !== 'search');
    const advancedCount = advancedKeys.filter((key) => filters[key] !== '').length;
    const hasAny = Object.values(filters).some((value) => value !== '');

    const textField = (key, label, placeholder) => (
        <Form.Group>
            <Form.Label className="small text-muted mb-1">{label}</Form.Label>
            <Form.Control size="sm" value={filters[key]} placeholder={placeholder} onChange={(e) => update(key, e.target.value)} />
        </Form.Group>
    );

    const selectField = (key, label, options) => (
        <Form.Group>
            <Form.Label className="small text-muted mb-1">{label}</Form.Label>
            <Form.Select size="sm" value={filters[key]} onChange={(e) => update(key, e.target.value)}>
                <option value="">All</option>
                {options.map(([value, text]) => (
                    <option key={value} value={value}>{text}</option>
                ))}
            </Form.Select>
        </Form.Group>
    );

    return (
        <>
            <Row className="mx-0 pb-3 align-items-end g-2">
                <Col xl={4} lg={12} className="p-0">
                    <IconField iconPosition="left">
                        <InputIcon className="pi pi-search" />
                        <InputText
                            value={filters.search}
                            onChange={(e) => update('search', e.target.value)}
                            placeholder="Search invoice no, client, item, remarks, amount..."
                            className="border-0 border-bottom ps-5 w-100"
                        />
                    </IconField>
                </Col>
                <Col xl={8} lg={12} className="d-flex flex-wrap gap-2 justify-content-xl-end justify-content-center align-items-end p-0">
                    {children}
                    <Button
                        label={advancedCount ? `Filters (${advancedCount})` : 'Filters'}
                        icon={showAdvanced ? 'pi pi-chevron-up' : 'pi pi-filter'}
                        size="small"
                        outlined={!showAdvanced}
                        className="py-2"
                        onClick={() => setShowAdvanced((prev) => !prev)}
                    />
                    {hasAny && (
                        <Button label="Clear" icon="pi pi-times" size="small" text severity="danger" className="py-2" onClick={() => setFilters(EMPTY_INVOICE_SEARCH)} />
                    )}
                </Col>
            </Row>

            {showAdvanced && (
                <Card className="shadow-0 border mb-3">
                    <Card.Body className="py-3">
                        <Row className="g-3">
                            <Col lg={3} md={4} sm={6}>{textField('invoiceNo', 'Invoice No', 'e.g. PI-21')}</Col>
                            <Col lg={3} md={4} sm={6}>{textField('client', 'Client', 'Name, company, GSTIN, email, phone')}</Col>
                            <Col lg={3} md={4} sm={6}>{textField('description', 'Item Description', 'e.g. Logo Design')}</Col>
                            <Col lg={3} md={4} sm={6}>{textField('productRemarks', 'Product Remarks', 'Line item remarks')}</Col>
                            <Col lg={3} md={4} sm={6}>{textField('invoiceRemarks', 'Invoice Remarks', 'Invoice remarks')}</Col>
                            <Col lg={3} md={4} sm={6}>{selectField('currency', 'Currency', CURRENCIES.map((code) => [code, code]))}</Col>
                            {showStatusFilters && (
                                <>
                                    <Col lg={3} md={4} sm={6}>{selectField('paymentStatus', 'Payment Status', PAYMENT_STATUSES)}</Col>
                                    <Col lg={3} md={4} sm={6}>{selectField('approvalStatus', 'Client Approval', APPROVAL_STATUSES)}</Col>
                                </>
                            )}
                            <Col lg={3} md={4} sm={6}>
                                <Form.Label className="small text-muted mb-1">Total Amount</Form.Label>
                                <div className="d-flex gap-2">
                                    <Form.Control size="sm" type="number" min="0" placeholder="Min" value={filters.minAmount} onChange={(e) => update('minAmount', e.target.value)} />
                                    <Form.Control size="sm" type="number" min="0" placeholder="Max" value={filters.maxAmount} onChange={(e) => update('maxAmount', e.target.value)} />
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}
        </>
    );
};

export default InvoiceSearchBar;
